import React, { useState, useEffect } from 'react';
import PortfolioHealth from '../reporting/portfolio-health';
import TimesheetGrid from '../timesheets/timesheet-grid';
import HealthReportForm from '../../components/reporting/health-report-form';
import TeamMemberCapacityManager from '../../components/team/team-member-capacity-manager';
import TeamHoursReport from '../reporting/team-hours-report';
import ForecastVsActual from '../../components/forecast-vs-actual';

interface TeamMetrics {
  teamName: string;
  memberCount: number;
  totalCapacityHours: number;
  allocatedHours: number;
  utilizationPercent: number;
}

interface PendingTimesheet {
  timesheetId: string;
  userId: string;
  userName: string;
  projectId: string;
  projectName: string;
  weekStartDate: string;
  totalHours: number;
  status: 'Submitted' | 'Draft';
  submittedDate: string;
}

interface BTLProject {
  ProjectId: string;
  Title: string;
  Domain: string;
  CreatedByName: string;
  OutlookStartDate?: string;
  OutlookEndDate?: string;
}

export default function TeamLeadHomepage() {
  const [teamMetrics, setTeamMetrics] = useState<TeamMetrics | null>(null);
  const [pendingTimesheets, setPendingTimesheets] = useState<PendingTimesheet[]>([]);
  const [btlProjects, setBTLProjects] = useState<BTLProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvalLoading, setApprovalLoading] = useState<string | null>(null);
  const [btlSaving, setBTLSaving] = useState<string | null>(null);
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [showHealthReport, setShowHealthReport] = useState(false);
  const [teamId, setTeamId] = useState<string | null>(null);

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    try {
      setLoading(true);

      // Get current user's info (Team Leads are identified by their email)
      const authEmail = localStorage.getItem('auth_email') || 'lead@example.com';

      // Fetch all teams - Team Leads see teams where they are the TeamLeadEmail
      const teamsRes = await fetch('/teams');
      const teamsData = await teamsRes.json();
      const allTeams = teamsData.data || [];

      // Find the team led by this user
      const myTeam = allTeams.find((t: any) => t.TeamLeadEmail === authEmail);

      if (myTeam) {
        setTeamId(myTeam.TeamId);

        // Fetch real team capacity metrics for this team
        const capacityRes = await fetch(`/team-capacity/${myTeam.TeamId}`);
        const capacityData = await capacityRes.json();

        if (capacityData.success && capacityData.data) {
          const metrics = capacityData.data;
          setTeamMetrics({
            teamName: metrics.TeamName,
            memberCount: metrics.TotalMembers,
            totalCapacityHours: metrics.TotalCapacityHours,
            allocatedHours: metrics.LoggedHours,
            utilizationPercent: metrics.UtilizationPercent,
          });
        }
      }

      // Fetch REAL pending timesheets
      const timesheetsRes = await fetch(`/timesheets/pending?teamLeadEmail=${encodeURIComponent(authEmail)}`);
      const timesheetsData = await timesheetsRes.json();

      if (timesheetsData.success && timesheetsData.data) {
        const pending = timesheetsData.data.map((ts: any) => ({
          timesheetId: ts.TimesheetId,
          userId: ts.UserId,
          userName: ts.UserName || 'Unknown User',
          projectId: ts.ProjectId,
          projectName: ts.ProjectTitle || 'Unknown Project',
          weekStartDate: ts.WeekStartDate,
          totalHours: ts.TotalHours,
          status: ts.Status,
          submittedDate: ts.SubmittedDate,
        }));
        setPendingTimesheets(pending);
      }

      // Fetch BTL projects
      const projectsRes = await fetch('/projects');
      const projectsData = await projectsRes.json();

      if (projectsData.success && projectsData.data) {
        const btl = projectsData.data.filter((p: any) => p.Status === 'BelowTheLine');
        setBTLProjects(btl);
      }
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutlookDates = async (projectId: string, startDate: string, endDate: string) => {
    setBTLSaving(projectId);
    try {
      const response = await fetch(`/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          outlookStartDate: startDate || null,
          outlookEndDate: endDate || null,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Update the local state
        setBTLProjects(prev => prev.map(p =>
          p.ProjectId === projectId
            ? { ...p, OutlookStartDate: startDate, OutlookEndDate: endDate }
            : p
        ));
        alert('Outlook dates saved!');
      } else {
        alert(`Failed to save: ${data.error}`);
      }
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setBTLSaving(null);
    }
  };

  const handleApproveTimesheet = async (timesheetId: string) => {
    setApprovalLoading(timesheetId);
    try {
      const approverEmail = localStorage.getItem('auth_email') || 'lead@example.com';
      const response = await fetch(`/timesheets/${timesheetId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approverEmail }),
      });

      const data = await response.json();
      if (data.success) {
        setPendingTimesheets(prev => prev.filter(ts => ts.timesheetId !== timesheetId));
        alert('Timesheet approved!');
      } else {
        alert(`Failed to approve: ${data.error}`);
      }
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setApprovalLoading(null);
    }
  };

  const handleRejectTimesheet = async (timesheetId: string) => {
    setApprovalLoading(timesheetId);
    try {
      // For now, we'll mark as rejected by setting status to Draft
      // In Phase 3, add a proper Rejected status
      const approverEmail = localStorage.getItem('auth_email') || 'lead@example.com';

      // Note: No reject endpoint yet, would need to add in Phase 3
      // For now, just remove from pending list with user feedback
      setPendingTimesheets(prev => prev.filter(ts => ts.timesheetId !== timesheetId));
      alert('Timesheet marked for revision (Phase 3: will call reject endpoint)');
    } catch (error: any) {
      alert(`Error: ${error.message}`);
    } finally {
      setApprovalLoading(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading team data...</div>;
  }

  return (
    <div style={{ padding: '0 20px' }}>
        {/* Team Capacity Section */}
        {teamMetrics && (
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ marginTop: 0 }}>Team Capacity</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              marginBottom: '2rem',
            }}>
              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Team Name
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>
                  {teamMetrics.teamName}
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Team Members
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>
                  {teamMetrics.memberCount} people
                </div>
              </div>

              <div style={{
                background: 'white',
                padding: '1.5rem',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              }}>
                <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Capacity
                </div>
                <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#333' }}>
                  {teamMetrics.allocatedHours} / {teamMetrics.totalCapacityHours}h
                </div>
                <div style={{ fontSize: '0.85rem', color: '#999', marginTop: '0.5rem' }}>
                  {teamMetrics.utilizationPercent}% utilized
                </div>
              </div>
            </div>

            {/* Capacity Bar */}
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Team Utilization</strong>
              </div>
              <div style={{
                height: '30px',
                background: '#eee',
                borderRadius: '4px',
                overflow: 'hidden',
              }}>
                <div
                  style={{
                    height: '100%',
                    width: `${teamMetrics.utilizationPercent}%`,
                    background: teamMetrics.utilizationPercent > 100 ? '#dc3545' :
                               teamMetrics.utilizationPercent > 80 ? '#ffc107' : '#28a745',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
              <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
                {teamMetrics.utilizationPercent}% (
                {teamMetrics.utilizationPercent > 100
                  ? `${teamMetrics.utilizationPercent - 100}% over capacity ⚠️`
                  : `${teamMetrics.totalCapacityHours - teamMetrics.allocatedHours}h available`
                })
              </div>
            </div>
          </div>
        )}

        {/* Pending Timesheets Section */}
        <div>
          <h2>Pending Timesheet Approvals ({pendingTimesheets.length})</h2>

          {pendingTimesheets.length === 0 ? (
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#666',
            }}>
              <p>No pending timesheets. All team timesheets are approved!</p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '1rem',
            }}>
              {pendingTimesheets.map(timesheet => (
                <div
                  key={timesheet.timesheetId}
                  style={{
                    background: 'white',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    borderLeft: '4px solid #ffc107',
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ color: '#666', fontSize: '0.9rem' }}>Team Member</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                        {timesheet.userName}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '0.9rem' }}>Project</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                        {timesheet.projectName}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '0.9rem' }}>Week</div>
                      <div style={{ fontSize: '1rem', color: '#333' }}>
                        {new Date(timesheet.weekStartDate).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', fontSize: '0.9rem' }}>Hours</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                        {timesheet.totalHours} hours
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <button
                      onClick={() => handleApproveTimesheet(timesheet.timesheetId)}
                      disabled={approvalLoading === timesheet.timesheetId}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        opacity: approvalLoading === timesheet.timesheetId ? 0.6 : 1,
                      }}
                    >
                      {approvalLoading === timesheet.timesheetId ? 'Approving...' : '✓ Approve'}
                    </button>
                    <button
                      onClick={() => handleRejectTimesheet(timesheet.timesheetId)}
                      disabled={approvalLoading === timesheet.timesheetId}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        opacity: approvalLoading === timesheet.timesheetId ? 0.6 : 1,
                      }}
                    >
                      {approvalLoading === timesheet.timesheetId ? 'Processing...' : '✗ Revise'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team Member Capacity Configuration */}
        {teamId && (
          <div style={{ marginBottom: '2rem' }}>
            <h2>Team Member Capacity Configuration</h2>
            <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '1rem' }}>
              Configure each team member's working hours and project allocation percentage to calculate accurate capacity.
            </p>
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <TeamMemberCapacityManager teamId={teamId} onSuccess={fetchTeamData} />
            </div>
          </div>
        )}

        {/* Hours Reporting */}
        {teamId && teamMetrics && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <TeamHoursReport teamId={teamId} teamName={teamMetrics.teamName} />
            </div>
          </div>
        )}

        {/* Forecast vs Actuals */}
        {teamId && (
          <div style={{ marginBottom: '2rem' }}>
            <div style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <ForecastVsActual teamId={teamId} title="Team Forecast vs Actuals" />
            </div>
          </div>
        )}

        {/* Below-The-Line Projects Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h2>Below-The-Line Projects (BTL)</h2>
          <p style={{ color: '#666', fontSize: '0.95rem', marginBottom: '1rem' }}>
            Track estimated timelines for ad-hoc and maintenance work not captured in the product portfolio.
          </p>

          {btlProjects.length === 0 ? (
            <div style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '8px',
              textAlign: 'center',
              color: '#666',
            }}>
              <p>No below-the-line projects at this time.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {btlProjects.map(project => {
                const [startDate, setStartDate] = React.useState(project.OutlookStartDate || '');
                const [endDate, setEndDate] = React.useState(project.OutlookEndDate || '');

                return (
                  <div
                    key={project.ProjectId}
                    style={{
                      background: 'white',
                      padding: '1.5rem',
                      borderRadius: '8px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      borderLeft: '4px solid #8B4513',
                    }}
                  >
                    <div style={{ marginBottom: '1rem' }}>
                      <h3 style={{ margin: '0 0 0.25rem 0', color: '#333' }}>{project.Title}</h3>
                      <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                        {project.ProjectId} • {project.Domain} • {project.CreatedByName}
                      </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                      <div>
                        <label style={{ display: 'block', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          Estimated Start Date
                        </label>
                        <input
                          type="date"
                          value={startDate}
                          onChange={(e) => setStartDate(e.target.value)}
                          disabled={btlSaving === project.ProjectId}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                          Estimated End Date
                        </label>
                        <input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          disabled={btlSaving === project.ProjectId}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.9rem',
                          }}
                        />
                      </div>
                    </div>

                    <button
                      onClick={() => handleSaveOutlookDates(project.ProjectId, startDate, endDate)}
                      disabled={btlSaving === project.ProjectId}
                      style={{
                        padding: '0.75rem 1.5rem',
                        background: '#06b6d4',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        opacity: btlSaving === project.ProjectId ? 0.6 : 1,
                      }}
                    >
                      {btlSaving === project.ProjectId ? 'Saving...' : 'Save Outlook Dates'}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showTimesheet && (
          <TimesheetGrid
            onSuccess={() => {
              setShowTimesheet(false);
              alert('Timesheets submitted successfully!');
            }}
            onCancel={() => setShowTimesheet(false)}
          />
        )}

        {showHealthReport && (
          <HealthReportForm
            onSuccess={() => {
              setShowHealthReport(false);
              alert('Health report submitted!');
            }}
            onCancel={() => setShowHealthReport(false)}
          />
        )}
      </div>
  );
}
